import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  Button,
  CardImg,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import Breadcrumbs from "components/Common/Breadcrumb";
import { fetchVideoSubscribe } from '../../../services/videoService';
import {
  FiAlertCircle,
  FiBookOpen,
  FiPlayCircle
} from 'react-icons/fi';

const ListVideo = () => {
  const [videoList, setVideoList] = useState([]);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentParentName, setCurrentParentName] = useState('MATERI VIDEO');

  // State for Video Modal
  const [modal, setModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');

  const pageTitle = "MATERI VIDEO";

  useEffect(() => {
    document.title = `${currentParentName} | Kumpulan ${pageTitle}`;
  }, [pageTitle, currentParentName]);

  const getImageUrl = useCallback((filePath) => {
    if (!filePath) {
      return '/images/default-material.png';
    }
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    return `${process.env.REACT_APP_API_BASE_URL || ''}${filePath.startsWith('/') ? '' : '/'}${filePath}`;
  }, []);

  // Function to extract YouTube video ID from various URL formats
  const extractYouTubeId = useCallback((url) => {
    if (!url) return null;
    
    // Handle standard YouTube URLs
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  }, []);

  // Function to extract video URL from iframe HTML
  const extractVideoUrlFromIframe = useCallback((iframeHtml) => {
    if (!iframeHtml) return '';
    
    // Handle escaped HTML characters
    const cleanHtml = iframeHtml
      .replace(/\\u003c/g, '<')
      .replace(/\\u003e/g, '>')
      .replace(/\\"/g, '"');

    // Create temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanHtml;

    // Find iframe and get src attribute
    const iframe = tempDiv.querySelector('iframe');
    return iframe ? iframe.getAttribute('src') || '' : '';
  }, []);

  // Function to get proper embed URL for playback
  const getEmbedUrl = useCallback((rawVideoSource) => {
    if (!rawVideoSource) return '';
    
    // Case 1: Source is an iframe HTML
    if (rawVideoSource.includes('<iframe')) {
      const srcUrl = extractVideoUrlFromIframe(rawVideoSource);
      const videoId = extractYouTubeId(srcUrl);
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : '';
    }
    
    // Case 2: Source is already a YouTube URL
    const videoId = extractYouTubeId(rawVideoSource);
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    
    // Case 3: Source is a direct video file URL
    if (rawVideoSource.startsWith('http://') || rawVideoSource.startsWith('https://')) {
      return rawVideoSource;
    }
    
    // Default case: assume it's a relative path to video file
    return `${process.env.REACT_APP_API_BASE_URL || ''}${rawVideoSource.startsWith('/') ? '' : '/'}${rawVideoSource}`;
  }, [extractVideoUrlFromIframe, extractYouTubeId]);

  const fetchVideoList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams(location.search);
      const id_jenis_soal = params.get('id_jenis_soal');
      const id_jenis_seleksi = params.get('id_jenis_seleksi');
      const id_kategori_paket = params.get('id_kategori_paket');
      const id_tipe_konten = params.get('id_tipe_konten');
      const kategori_paket = params.get('kategori_paket');
      const kategoriFromUrl = params.get('kategori');
      

      setCurrentParentName(kategoriFromUrl ? kategoriFromUrl.toUpperCase() : 'MATERI VIDEO');

      const response = await fetchVideoSubscribe({
        id_jenis_soal,
        id_jenis_seleksi,
        id_tipe_konten,
        id_kategori_paket,
        disable_pagination: true,
        tipe: kategori_paket,
      });

      if (response.success && Array.isArray(response.data)) {
        setVideoList(response.data);
      } else {
        setError(response.message || 'Data video kosong atau tidak valid.');
      }
    } catch (err) {
      setError(err.message || 'Gagal memuat video.');
    } finally {
      setLoading(false);
    }
  }, [location.search]);

  useEffect(() => {
    fetchVideoList();
  }, [fetchVideoList]);

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setVideoUrl('');
      setVideoTitle('');
    }
  };

  const handlePlayVideo = (videoSource, title) => {
    const embedUrl = getEmbedUrl(videoSource);
    if (embedUrl) {
      setVideoUrl(embedUrl);
      setVideoTitle(title);
      setModal(true);
    } else {
      setError('Format video tidak didukung');
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Materi Video" breadcrumbItem={pageTitle} />
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner color="primary" />
            <span className="ms-2">Memuat materi video...</span>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Materi Video" breadcrumbItem={pageTitle} />
          <Alert color="danger">
            <FiAlertCircle className="me-2" size={18} />
            {error}
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Materi Video" breadcrumbItem={currentParentName} />

        {videoList.length === 0 ? (
          <Alert color="info" className="text-center py-3">
            Tidak ditemukan materi Video.
          </Alert>
        ) : (
          <Row className="gy-4">
            {videoList.map((item, index) => {
              const imageUrl = getImageUrl(item.thumbnail_image);

              return (
                <Col key={index} lg={3} md={4} sm={6}>
                  <Card className="materi-card h-100">
                    <div 
                      className="materi-image-container position-relative"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handlePlayVideo(item.thumbnail_url, item.judul)}
                    >
                      {imageUrl ? (
                        <CardImg
                          top
                          src={imageUrl}
                          alt={item.judul}
                          className="materi-image"
                          style={{ height: '180px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.className = 'materi-image-fallback';
                            e.target.src = '/images/default-material.png';
                          }}
                        />
                      ) : (
                        <div className="materi-image-placeholder d-flex flex-column justify-content-center align-items-center" style={{ height: '180px' }}>
                          <FiBookOpen size={48} className="text-muted" />
                          <p className="mt-2 text-muted">No Image</p>
                        </div>
                      )}
                      <div className="play-icon-overlay">
                        <FiPlayCircle size={48} color="#fff" />
                      </div>
                    </div>
                    <CardBody className="d-flex flex-column">
                      <h5 className="materi-title mb-2">{item.judul}</h5>
                      <div
                        className="materi-desc text-muted small mb-3"
                        dangerouslySetInnerHTML={{ __html: item.deskripsi || 'Deskripsi tidak tersedia' }}
                      />
                      <Button
                        color="primary"
                        outline
                        className="mt-auto"
                        onClick={() => handlePlayVideo(item.thumbnail_url, item.judul)}
                      >
                        <FiPlayCircle className="me-1" /> Tonton Video
                      </Button>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>

      {/* Video Player Modal */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg" centered>
        <ModalHeader toggle={toggleModal}>{videoTitle}</ModalHeader>
        <ModalBody>
          {videoUrl ? (
            <div className="ratio ratio-16x9">
              <iframe
                src={videoUrl}
                title="Video Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ border: 'none' }}
              ></iframe>
            </div>
          ) : (
            <Alert color="warning">URL video tidak tersedia</Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Tutup
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ListVideo;